<template>
  <HeaderBar class="mx-auto" />
  <section>
    <div class="carvia-container flex flex-col gap-8">
      <div class="flex flex-col-reverse items-stretch gap-8 md:flex-row">
        <div
          v-if="$can('read', 'News') || $can('readSummary', 'AgentProvision')"
          class="flex flex-1 flex-col justify-items-start gap-8"
        >
          <div v-if="$can('readSummary', 'AgentProvision')" class="flex flex-col gap-4">
            <DashboardAgentProvision />
          </div>
          <div v-if="$can('read', 'News')">
            <div class="flex items-center justify-between">
              <h2>
                {{ t('news') }}
              </h2>
              <router-link :to="{ name: 'newsList' }" class="text-link">
                {{ t('showAll') }}
              </router-link>
            </div>
            <div class="mt-5 flex flex-col space-y-4">
              <DashboardNewsList />
            </div>
          </div>
          <div v-if="can('list', 'User')" class="flex flex-col gap-3">
            <h2>
              {{ t('activeUsers') }}
            </h2>
            <p v-if="isActiveUsersLoading" class="text-sm text-slate-500">
              {{ t('loadingActiveUsers') }}
            </p>
            <ul v-else-if="activeUsers.length" class="flex flex-col gap-2">
              <li
                v-for="entry in activeUsers"
                :key="entry.user.id"
                class="flex items-center justify-between rounded bg-white px-3 py-2 text-sm"
              >
                <span>{{ `${entry.user.firstName} ${entry.user.lastName}` }}</span>
                <span class="flex items-center gap-2">
                  <a
                    v-if="entry.user.callerId"
                    :href="`tel:${entry.user.callerId}`"
                    class="text-link"
                  >
                    {{ entry.user.callerId }}
                  </a>
                  <span v-else class="text-slate-500">{{ t('noPhone') }}</span>
                  <CallButton
                    v-if="entry.user.did"
                    :phone-number="entry.user.did"
                    class="flex h-auto items-center gap-1 whitespace-nowrap rounded-sm bg-green-100 px-2 py-0.5 text-sm text-green-800 hover:bg-green-200"
                    >{{ entry.user.did }}</CallButton
                  >
                </span>
              </li>
            </ul>
            <p v-else class="text-sm text-slate-500">
              {{ t('noActiveUsers') }}
            </p>
          </div>
          <div class="mt-8">
            <h2>
              {{ t('rosUpdates') }}
            </h2>
            <div class="mt-5">
              <DashboardRosUpdatesList />
            </div>
          </div>
          <DashboardCarviaWorldLink />
        </div>
        <div class="flex-1">
          <div class="flex items-center justify-between gap-3">
            <h2>
              {{ t('start') }}
            </h2>
            <button
              v-if="canOpen"
              type="button"
              :class="
                input({
                  size: 'sm',
                  class: 'w-auto items-center gap-2 text-left text-obsidian-400 md:hidden',
                })
              "
              @click="openCommandPalette"
            >
              <SearchIcon class="size-4 shrink-0 text-obsidian-400" />
              <span class="truncate">{{ t('quickSearch') }}</span>
            </button>
          </div>
          <div class="mt-6 flex flex-col space-y-5">
            <DashboardButton
              v-for="button in buttons.filter((button) => button.available)"
              :key="button.text"
              @click="$router.push({ name: button.route })"
            >
              {{ t(button.text) }}
            </DashboardButton>
          </div>
        </div>
      </div>
      <div v-if="$can('readSummary', 'EmployeeDashboard')">
        <DashboardEmployeeStats />
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import HeaderBar from '@/components/headerbar/HeaderBar.vue';
import { computed, ref, watchEffect } from 'vue';
import { useI18n } from 'vue-i18n';
import { useUiStore } from '@/stores/ui.store';
import DashboardButton from './components/DashboardButton.vue';
import DashboardCarviaWorldLink from './components/DashboardCarviaWorldLink.vue';
import DashboardNewsList from './components/DashboardNewsList.vue';
import DashboardRosUpdatesList from './components/DashboardRosUpdatesList.vue';
import { useAbility } from '@casl/vue';
import DashboardAgentProvision from '@/views/dashboard/components/DashboardAgentProvision.vue';
import DashboardEmployeeStats from '@/views/dashboard/components/DashboardEmployeeStats.vue';
import { useTapToPayLinking } from '@/hooks/use-tap-to-pay-linking';
import { useTokenPayload } from '@/queries/auth/use-login.ts';
import { isJWTUser } from '@/entities/auth/jwt-user.entity';
import { useActiveTimeTrackingEntries } from '@/queries/use-time-tracking';
import { UserRole } from '@/entities/auth/user-role.enum';
import CallButton from '@/components/CallButton.vue';
import { input } from '@carvia/theme/lib/cva/input';
import { SearchIcon } from 'lucide-vue-next';
import { useCommandPalette } from '@/hooks/use-command-palette';

const { t } = useI18n();
const { canOpen, openCommandPalette } = useCommandPalette();
const uiStore = useUiStore();
const { data: jwtUser } = useTokenPayload();
const { can } = useAbility();

watchEffect(() => {
  const user = jwtUser.value;
  uiStore.setHeaderTitle(`${t('servus')} ${isJWTUser(user) ? user.firstName : ''}`);
});

useTapToPayLinking();
const { data: activeEntries, isPending: isActiveUsersLoading } = useActiveTimeTrackingEntries(
  { role: [UserRole.OPERATOR, UserRole.ADMIN] },
  can('list', 'User'),
);

const activeUsers = computed(() => activeEntries.value ?? []);

const buttons = ref<{ text: string; route: string; available: boolean }[]>([
  {
    text: 'handover',
    route: 'upcomingHandovers',
    available: can('handle', 'Booking'),
  },
  {
    text: 'occupancy',
    route: 'bookingPlan',
    available: can('list', 'Allocation'),
  },
  {
    text: 'bookingList',
    route: 'bookingList',
    available: can('list', 'Booking'),
  },
  {
    text: 'vehicleTypes',
    route: 'vehicleTypes',
    available: can('read', 'VehicleType') && !can('ros', 'VehiclePool'),
  },
  {
    text: 'vehiclePoolManagement',
    route: 'vehiclePool',
    available: can('ros', 'VehiclePool'),
  },
  {
    text: 'createBooking',
    route: 'bookingCreate',
    available: can('ros', 'CreateBooking'),
  },
]);
</script>

<i18n lang="json">
{
  "en": {
    "news": "News",
    "rosUpdates": "ROS Updates",
    "start": "Let's go",
    "quickSearch": "Quick search",
    "vehicleTypes": "Vehicles",
    "vehiclePoolManagement": "Vehicle Pool Management",
    "handover": "Handovers",
    "bookingList": "Booking list",
    "createBooking": "Create Booking",
    "occupancy": "Occupancy",
    "showAll": "Show all",
    "activeUsers": "Concierge Service online",
    "loadingActiveUsers": "Loading active users...",
    "noActiveUsers": "No active users currently",
    "noPhone": "No direct number",
    "servus": "Howdy"
  },
  "de": {
    "news": "Neuigkeiten",
    "rosUpdates": "ROS-Updates",
    "start": "Loslegen",
    "quickSearch": "Schnellsuche",
    "vehicleTypes": "Fahrzeugliste",
    "vehiclePoolManagement": "Fuhrparkmanagement",
    "handover": "Übergaben & Rückgaben",
    "bookingList": "Buchungsliste",
    "createBooking": "Buchung erstellen",
    "occupancy": "Belegungsplan",
    "showAll": "Alle anzeigen",
    "activeUsers": "Concierge Service online",
    "loadingActiveUsers": "Aktive Benutzer werden geladen...",
    "noActiveUsers": "Aktuell keine aktiven Benutzer",
    "noPhone": "Keine Durchwahl",
    "servus": "Servus"
  },
  "es": {
    "news": "Novedades",
    "rosUpdates": "Actualizaciones ROS",
    "start": "Empezar",
    "quickSearch": "Búsqueda rápida",
    "vehicleTypes": "Vehículos",
    "vehiclePoolManagement": "Gestión de flota",
    "handover": "Entregas y devoluciones",
    "bookingList": "Lista de reservas",
    "createBooking": "Crear reserva",
    "occupancy": "Ocupación",
    "showAll": "Ver todo",
    "activeUsers": "Concierge en línea",
    "loadingActiveUsers": "Cargando usuarios activos…",
    "noActiveUsers": "No hay usuarios activos en este momento",
    "noPhone": "Sin número directo",
    "servus": "Hola"
  }
}
</i18n>
